import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import NoteCard from "../../components/NoteCard";
import FolderCard from "../../components/User/FolderCard";
import SwiftAlert from "../../components/SwiftAlert";
import ShareModal from "../../components/User/ShareModal";
import { 
  getUserArchivedReviewers, 
  getArchivedFolders, 
  updateReviewer, 
  updateFolder, 
  deleteReviewer, 
  deleteFolder, 
  getReviewerById 
} from "../../services/api";
import { checkEnabled, computeVerified, normalizeColorKey } from "../../utils/libraryUtils";
import { generatePDFDocument } from "../../utils/pdfGenerator";

const LIST_ITEM_VARIANTS = {
  blue:    { iconBg: "bg-blue-100",    iconText: "text-blue-600",    activeBorder: "border-blue-500",    activeBg: "bg-blue-50",    ring: "ring-blue-500" },
  orange:  { iconBg: "bg-orange-100",  iconText: "text-orange-600",  activeBorder: "border-orange-500",  activeBg: "bg-orange-50",  ring: "ring-orange-500" },
  purple:  { iconBg: "bg-purple-100",  iconText: "text-purple-600",  activeBorder: "border-purple-500",  activeBg: "bg-purple-50",  ring: "ring-purple-500" },
  emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600", activeBorder: "border-emerald-500", activeBg: "bg-emerald-50", ring: "ring-emerald-500" },
  red:     { iconBg: "bg-red-100",     iconText: "text-red-600",     activeBorder: "border-red-500",     activeBg: "bg-red-50",     ring: "ring-red-500" },
  gray:    { iconBg: "bg-slate-100",   iconText: "text-slate-600",   activeBorder: "border-slate-500",   activeBg: "bg-slate-50",   ring: "ring-slate-500" },
};

const ArchivePage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [archivedNotes, setArchivedNotes] = useState([]);
  const [archivedFolders, setArchivedFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  // Delete note modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  // Move note modal
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [noteToMove, setNoteToMove] = useState(null);

  // Delete folder modal
  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  // Share modal
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [noteToShare, setNoteToShare] = useState(null);

  const navigate = useNavigate();

  const fetchArchived = useCallback(async () => {
    setLoading(true);
    try {
      const [notesRes, foldersRes] = await Promise.all([
        getUserArchivedReviewers(),
        getArchivedFolders(),
      ]);
      const notesData = Array.isArray(notesRes) ? notesRes : notesRes?.data || [];
      const enriched = notesData.map((n) => ({ ...n, verified: computeVerified(n) }));
      setArchivedNotes(enriched);

      const foldersData = Array.isArray(foldersRes) ? foldersRes : foldersRes?.data || [];
      setArchivedFolders(foldersData);
    } catch (err) {
      console.error("[ArchivePage] Error loading archive:", err);
      setAlert({ type: "error", title: "Failed", message: "Could not load archive." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArchived();
  }, [fetchArchived]);

  // Standalone archived notes (notes not inside an archived folder)
  const archivedFolderIdSet = useMemo(() => {
    return new Set(archivedFolders.map((f) => String(f._id || f.id)));
  }, [archivedFolders]);

  const standaloneNotes = useMemo(() => {
    return archivedNotes.filter((n) => {
      const fId = n.folder?._id || n.folder;
      return !fId || !archivedFolderIdSet.has(String(fId));
    });
  }, [archivedNotes, archivedFolderIdSet]);

  // --- Handlers: Folders ---

  const handleUnarchiveFolder = async (folder) => {
    try {
      setArchivedFolders((prev) => prev.filter((f) => f._id !== folder._id));
      setArchivedNotes((prev) => prev.filter((n) => {
        const fId = n.folder?._id || n.folder;
        return String(fId) !== String(folder._id);
      }));
      await updateFolder(folder._id, { archived: false });
      setAlert({ type: "success", title: "Unarchived", message: `Folder "${folder.name}" and its notes were restored to Library.` });
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: "Failed to unarchive folder." });
    }
  };

  const openDeleteFolderModal = (folder) => {
    setFolderToDelete(folder);
    setIsDeleteFolderModalOpen(true);
  };

  const handleDeleteFolderSubmit = async () => {
    if (!folderToDelete) return;
    try {
      setArchivedFolders((prev) => prev.filter((f) => f._id !== folderToDelete._id));
      setArchivedNotes((prev) => prev.filter((n) => {
        const fId = n.folder?._id || n.folder;
        return String(fId) !== String(folderToDelete._id);
      }));
      setIsDeleteFolderModalOpen(false);
      setFolderToDelete(null);
      setAlert({ type: "success", title: "Deleted", message: `Folder "${folderToDelete.name}" deleted permanently.` });
      await deleteFolder(folderToDelete._id);
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: "Failed to delete folder." });
    }
  };

  // --- Handlers: Notes ---

  const handleUnarchiveNote = async (note) => {
    try {
      setArchivedNotes((prev) => prev.filter((n) => n._id !== note._id));
      await updateReviewer(note._id, { archived: false });
      setAlert({ type: "success", title: "Unarchived", message: `"${note.title || note.topic}" restored to Library.` });
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: "Failed to unarchive note." });
    }
  };

  const openDeleteModal = (note) => {
    setNoteToDelete(note);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSubmit = async () => {
    if (!noteToDelete) return;
    try {
      setArchivedNotes((prev) => prev.filter((n) => n._id !== noteToDelete._id));
      setIsDeleteModalOpen(false);
      setNoteToDelete(null);
      setAlert({ type: "success", title: "Deleted", message: "Note deleted permanently." });
      await deleteReviewer(noteToDelete._id);
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: "Failed to delete note." });
    }
  };

  const openMoveModal = (note) => {
    setNoteToMove(note);
    setIsMoveModalOpen(true);
  };

  const handleMoveSubmit = async (targetFolderId) => {
    if (!noteToMove) return;
    const sourceFolderId = noteToMove.folder?._id || noteToMove.folder;

    if (String(sourceFolderId || "") === String(targetFolderId || "")) {
      setIsMoveModalOpen(false);
      setNoteToMove(null);
      return;
    }

    const noteId = noteToMove._id || noteToMove.id;
    const noteTitle = noteToMove.title || noteToMove.topic || "Note";

    try {
      // 1. Optimistic Update in archivedNotes
      setArchivedNotes((prev) =>
        prev.map((n) =>
          (n._id === noteId || n.id === noteId)
            ? { ...n, folder: targetFolderId }
            : n
        )
      );

      // 2. Update Folder Counts
      setArchivedFolders((prev) =>
        prev.map((f) => {
          if (sourceFolderId && String(f._id) === String(sourceFolderId)) {
            return { ...f, count: Math.max(0, (f.count || 0) - 1) };
          }
          if (targetFolderId && String(f._id) === String(targetFolderId)) {
            return { ...f, count: (f.count || 0) + 1 };
          }
          return f;
        })
      );

      setIsMoveModalOpen(false);
      setNoteToMove(null);

      const targetFolder = archivedFolders.find((f) => String(f._id) === String(targetFolderId));
      setAlert({
        type: "success",
        title: "Moved",
        message: targetFolder
          ? `"${noteTitle}" moved to "${targetFolder.name}".`
          : `"${noteTitle}" moved to standalone archive (No Folder).`,
      });

      await updateReviewer(noteId, { folderId: targetFolderId });
    } catch (error) {
      console.error("[ArchivePage] Move note failed:", error);
      setAlert({ type: "error", title: "Failed", message: "Failed to move note." });
    }
  };

  const handleRenameNote = async (note, newTitle) => {
    const cleanTitle = newTitle.trim();
    if (!cleanTitle) return;
    try {
      setArchivedNotes((prev) =>
        prev.map((n) => (n._id === note._id ? { ...n, title: cleanTitle, topic: cleanTitle } : n))
      );
      await updateReviewer(note._id, { title: cleanTitle });
      setAlert({ type: "success", title: "Renamed", message: "Note renamed successfully." });
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: error?.response?.data?.error || "Failed to rename note." });
    }
  };

  const handleColorChange = async (note, newColor) => {
    try {
      setArchivedNotes((prev) =>
        prev.map((n) => (n._id === note._id ? { ...n, color: newColor } : n))
      );
      await updateReviewer(note._id, { color: newColor });
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: "Failed to update color." });
    }
  };

  const handleStudyNavigation = (file, requestedMode) => {
    const cfg = file?.config || {};
    let mode = requestedMode;
    if (!mode) {
      if (checkEnabled(cfg.summaryEnabled)) mode = "summary";
      else if (checkEnabled(cfg.flashcardsEnabled)) mode = "flashcards";
      else if (checkEnabled(cfg.quizEnabled)) mode = "quiz";
    }
    if (!mode) return;
    navigate("/study-mode", {
      state: {
        mode,
        noteData: {
          ...file,
          _id: file?._id,
          id: file?._id || file?.id,
          title: file?.title || file?.topic || "Untitled",
          subject: file?.meta || "Archive",
          hasSummary: checkEnabled(cfg.summaryEnabled),
          hasFlashcards: checkEnabled(cfg.flashcardsEnabled),
          hasQuiz: checkEnabled(cfg.quizEnabled),
          config: cfg,
        },
        reviewerId: file?._id || file?.id,
      },
    });
  };

  const handleDownloadPdf = async (note) => {
    const reviewerId = note?._id || note?.id;
    if (!reviewerId) {
      setAlert({ type: "error", title: "Failed", message: "Reviewer ID is missing." });
      return;
    }
    try {
      const raw = await getReviewerById(reviewerId);
      const normalized = raw && raw.data && raw.status ? raw.data : raw;
      const meta = normalized?.meta || normalized?.data?.meta || {};
      const data =
        normalized?.meta && normalized?.data
          ? normalized.data
          : normalized?.data?.data ?? normalized?.data ?? normalized;
      const summary =
        (typeof data === "string" ? data : "") ||
        data?.summaryHtml || data?.summary || data?.html || data?.content || "";
      const flashcardsRaw = data?.flashcards || data?.flashCards || data?.cards || [];
      const flashcards = Array.isArray(flashcardsRaw)
        ? flashcardsRaw
            .map((c) => ({ front: c?.front || c?.term || "", back: c?.back || c?.definition || "" }))
            .filter((c) => c.front || c.back)
        : [];
      const quizRaw = data?.quiz || data?.questions || [];
      const quiz = Array.isArray(quizRaw)
        ? quizRaw
            .map((q) => ({
              question: q?.question || "",
              options: Array.isArray(q?.options) ? q.options : [],
              answer: q?.answer || "",
              explanation: q?.explanation || "",
            }))
            .filter((q) => q.question)
        : [];
      if (!summary && flashcards.length === 0 && quiz.length === 0) {
        setAlert({ type: "warning", title: "No Content", message: "This reviewer has no downloadable content yet." });
        return;
      }
      const cfg = { ...(meta?.config || {}), ...(note?.config || {}) };
      generatePDFDocument({
        title: note?.title || note?.topic || "Reviewer",
        generatedData: { summary, flashcards, quiz },
        config: {
          ...cfg,
          summaryEnabled: checkEnabled(cfg.summaryEnabled),
          flashcardsEnabled: checkEnabled(cfg.flashcardsEnabled),
          quizEnabled: checkEnabled(cfg.quizEnabled),
        },
      });
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: error?.message || "Failed to download PDF." });
    }
  };

  const openShareModal = (note) => {
    setNoteToShare(note);
    setIsShareModalOpen(true);
  };

  const handleShareModalUpdateNote = (updatedNote) => {
    setArchivedNotes((prev) =>
      prev.map((n) => ((n._id || n.id) === (updatedNote._id || updatedNote.id) ? updatedNote : n))
    );
  };

  return (
    <div className="bg-slate-50 text-slate-800 flex h-screen overflow-hidden font-sans antialiased">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} title="Archive" />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto w-full animate-fade-in pb-20 md:pb-0">

            {/* Archive Banner */}
            <div className="w-full bg-gradient-to-r from-blue-700 to-blue-600 rounded-2xl p-6 text-white shadow-lg mb-8 relative overflow-hidden">
              <div className="absolute right-0 top-0 h-full w-1/3 bg-white/10 skew-x-12 transform translate-x-12"></div>
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <i className="fa-solid fa-box-archive text-2xl text-blue-100"></i>
                    <h1 className="text-2xl font-bold">Archive</h1>
                  </div>
                  <p className="text-blue-100 text-sm">
                    {loading
                      ? "Loading..."
                      : `${archivedFolders.length} archived ${archivedFolders.length === 1 ? "folder" : "folders"} • ${standaloneNotes.length} standalone archived ${standaloneNotes.length === 1 ? "note" : "notes"}. Notes and folders here are safe — unarchive to restore them to your Library.`}
                  </p>
                </div>
                <button
                  onClick={() => navigate("/library")}
                  className="bg-white text-blue-700 px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-50 transition shadow-sm flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="fa-solid fa-arrow-left"></i> Back to Library
                </button>
              </div>
            </div>

            {/* Archived Folders Grid */}
            {archivedFolders.length > 0 && (
              <section className="mb-10">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4 ml-1">
                  Archived Folders
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {archivedFolders.map((folder) => (
                    <FolderCard
                      key={folder._id}
                      name={folder.name}
                      count={folder.count}
                      color={folder.color}
                      onClick={() => navigate(`/library/archive/${folder._id}`, { state: { folder } })}
                      onUnarchive={() => handleUnarchiveFolder(folder)}
                      onDelete={() => openDeleteFolderModal(folder)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Archived Notes Grid */}
            <section>
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4 ml-1">
                Archived Notes
              </h3>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-48 bg-white rounded-xl border border-gray-100 animate-pulse shadow-sm" />
                  ))}
                </div>
              ) : standaloneNotes.length === 0 && archivedFolders.length === 0 ? (
                <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center shadow-sm">
                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fa-solid fa-box-archive text-blue-500 text-2xl"></i>
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg mb-1">Archive is Empty</h3>
                  <p className="text-sm text-slate-400 mb-6">
                    Notes and folders you archive from your Library will appear here.
                  </p>
                  <button
                    onClick={() => navigate("/library")}
                    className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 transition shadow-md"
                  >
                    Go to Library
                  </button>
                </div>
              ) : standaloneNotes.length === 0 ? (
                <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center shadow-sm">
                  <p className="text-sm text-slate-400">No standalone archived notes.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {standaloneNotes.map((note) => (
                    <NoteCard
                      key={note._id || note.id}
                      note={note}
                      folderName="Archived"
                      onStudy={handleStudyNavigation}
                      onRename={handleRenameNote}
                      onColorChange={handleColorChange}
                      onMove={openMoveModal}
                      onDelete={openDeleteModal}
                      onDownloadPdf={handleDownloadPdf}
                      onShare={openShareModal}
                      onUnarchive={handleUnarchiveNote}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      {/* DELETE FOLDER MODAL */}
      {isDeleteFolderModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xs w-full p-6 animate-in zoom-in-95 duration-200 text-center">
            <div className="w-14 h-14 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
              <i className="fa-solid fa-folder-minus"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Folder?</h3>
            <p className="text-gray-500 text-sm mb-6">
              Are you sure you want to delete <b>{folderToDelete?.name}</b>?
              <br/><span className="text-xs text-red-500 mt-1 block font-semibold">All notes inside will also be permanently deleted.</span>
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleDeleteFolderSubmit}
                className="w-full py-2.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 transition shadow-md"
              >
                Yes, Delete it
              </button>
              <button
                onClick={() => setIsDeleteFolderModalOpen(false)}
                className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE NOTE MODAL */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xs w-full p-6 animate-in zoom-in-95 duration-200 text-center">
            <div className="w-14 h-14 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
              <i className="fa-solid fa-trash-can"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Note?</h3>
            <p className="text-gray-500 text-sm mb-6">
              Are you sure you want to permanently delete{" "}
              <b>{noteToDelete?.title || noteToDelete?.topic}</b>? This cannot be undone.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleDeleteSubmit}
                className="w-full py-2.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 transition shadow-md"
              >
                Yes, Delete it
              </button>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MOVE NOTE MODAL (Inside Archive Only) */}
      {isMoveModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Move to Archive Folder</h3>
            <p className="text-sm text-gray-500 mb-4">
              Select an archived destination for <b>{noteToMove?.title || noteToMove?.topic}</b>
            </p>

            <div className="max-h-60 overflow-y-auto space-y-2 mb-4 pr-1">
              {/* Option: No Folder */}
              <button
                onClick={() => handleMoveSubmit(null)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${
                  !noteToMove?.folder
                    ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                  <i className="fa-solid fa-folder-minus"></i>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 block">No Folder</span>
                  <span className="text-[11px] text-gray-400">Standalone note in Archive</span>
                </div>
                {!noteToMove?.folder && (
                  <i className="fa-solid fa-check text-blue-600 ml-auto"></i>
                )}
              </button>

              {/* Archived Folders Only */}
              {archivedFolders.map((f) => {
                const isCurrent =
                  String(noteToMove?.folder?._id || noteToMove?.folder) === String(f._id);
                const theme = LIST_ITEM_VARIANTS[normalizeColorKey(f.color)] || LIST_ITEM_VARIANTS.blue;

                return (
                  <button
                    key={f._id}
                    onClick={() => handleMoveSubmit(f._id)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${
                      isCurrent
                        ? `${theme.activeBorder} ${theme.activeBg} ring-1 ${theme.ring}`
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme.iconBg} ${theme.iconText}`}>
                      <i className="fa-solid fa-folder"></i>
                    </div>
                    <span className="font-semibold text-gray-700 truncate">{f.name}</span>
                    {isCurrent && (
                      <i className="fa-solid fa-check text-blue-600 ml-auto"></i>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setIsMoveModalOpen(false)}
              className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* SHARE MODAL */}
      {isShareModalOpen && noteToShare && (
        <ShareModal
          note={noteToShare}
          onClose={() => {
            setIsShareModalOpen(false);
            setNoteToShare(null);
          }}
          onUpdateNote={handleShareModalUpdateNote}
          onAlert={setAlert}
        />
      )}

      {/* ALERT */}
      {alert && (
        <SwiftAlert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default ArchivePage;
